/**
 * FoodPulseSection — generic landing page for any FoodPulse section.
 *
 * Reads the section slug from the URL (`/food-pulse/:section`),
 * looks it up in the ``FP_SECTIONS`` manifest, and renders:
 *   1. Editorial hero — kicker + serif H1 + tagline + blurb
 *   2. Sub-section tile grid — one card per child, deep-linking
 *      into existing directories / article filters / feature pages
 *   3. "Coming soon" panel where a subsection has no real backend
 *      wiring yet (deals, marketplace, health) — with CTAs pointing
 *      to related existing tools (Businesses directory, Articles)
 *
 * Route: /food-pulse/:section  (see App.js)
 */
import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight, Sparkles,
  Compass, Utensils, BookOpen, Heart, Tag, Users, Sprout,
  Recycle, MapPin, Megaphone, ShoppingBag, Info, Home,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { findFpSection } from "@/config/foodPulseSections";
import { categoryIllustration } from "@/config/categoryIllustrations";

const ICONS = {
  home: Home, info: Info, compass: Compass, utensils: Utensils,
  "book-open": BookOpen, heart: Heart, tag: Tag, users: Users,
  sprout: Sprout, recycle: Recycle, "map-pin": MapPin,
  newspaper: Megaphone, megaphone: Megaphone, "shopping-bag": ShoppingBag,
};

/**
 * Very rough heuristic: a child link is "wired" (real content lives
 * behind it) if it points at an existing directory / articles /
 * encyclopedia route. Everything else is "coming soon".
 */
function isWired(to) {
  if (!to) return false;
  const wired = [
    "/food-pulse/businesses",
    "/food-pulse/food",
    "/food-pulse/articles",
    "/food-pulse",   // home
    "/contact",
    "/",
  ];
  return wired.some((w) => to.startsWith(w + "?") || to === w || to.startsWith(w + "#") || to.startsWith(w + "/"));
}

/**
 * Turn a subsection label into a plausible category illustration.
 * We infer the business_type when the sub-item is a directory link,
 * otherwise fall back to a generic food illustration.
 */
function inferIllustration(child, index) {
  const to = child.to || "";
  const m = to.match(/[?&]type=([^&]+)/);
  const first = m ? decodeURIComponent(m[1]).split(",")[0] : null;
  // Deterministic variety by (label + index) so tile grid mixes visually.
  return categoryIllustration(first || "other", `${child.label}-${index}`);
}

export default function FoodPulseSection() {
  const { section: slug } = useParams();
  const section = useMemo(() => findFpSection(slug), [slug]);

  useEffect(() => {
    if (section) document.title = `${section.label} — Food Pulse`;
  }, [section]);

  if (!section) {
    return (
      <div className="min-h-screen p-8" style={{ background: "#FBF7F0" }}>
        <div
          className="max-w-3xl mx-auto rounded-2xl border p-10 text-center bg-white"
          style={{ borderColor: "#C79A3F" }}
          data-testid="fp-section-not-found"
        >
          <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
          <div className="font-display text-lg mb-1">Section not found.</div>
          <Link to="/food-pulse" className="text-sm underline" style={{ color: "#8B5E10" }}>
            ← Back to Food Pulse home
          </Link>
        </div>
      </div>
    );
  }

  const Icon = ICONS[section.icon] || Utensils;
  const children = section.children || [];

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid={`fp-section-${section.slug}`}>
      <SeoHead
        section="food-pulse"
        title={`${section.label} — Food Pulse`}
        description={section.tagline || section.blurb || `${section.label} on Food Pulse.`}
      />

      {/* Hero */}
      <section style={{ background: "#F0E2C7" }} className="border-b" data-testid="fp-section-hero">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div
            className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest rounded-full px-3 py-1 mb-4"
            style={{ background: "#fff", border: "1px solid #C79A3F", color: "#8B5E10" }}
          >
            <Icon className="w-3 h-3" /> Food Pulse · {section.label}
          </div>
          <h1
            className="font-display font-bold leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-3"
            style={{ color: "#1A1613" }}
            data-testid="fp-section-h1"
          >
            {section.tagline || section.label}
          </h1>
          {section.blurb && (
            <p className="text-base sm:text-lg max-w-3xl" style={{ color: "#4B3A20" }} data-testid="fp-section-blurb">
              {section.blurb}
            </p>
          )}
        </div>
      </section>

      {/* Sub-section tile grid */}
      {children.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-12" data-testid="fp-section-tiles">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {children.map((c, i) => {
              const wired = isWired(c.to);
              const img = inferIllustration(c, i);
              return (
                <Link
                  key={c.to || c.label}
                  to={c.to}
                  className="group rounded-2xl bg-white border overflow-hidden hover:shadow-md transition-shadow"
                  style={{ borderColor: "#E9D8B4" }}
                  data-testid={`fp-tile-${(c.to || c.label).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                    <img
                      src={img}
                      alt={c.label}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-display text-xl leading-tight" style={{ color: "#1A1613" }}>
                        {c.label}
                      </div>
                      {!wired && (
                        <span
                          className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{ background: "#F0E2C7", color: "#8B5E10" }}
                        >
                          Soon
                        </span>
                      )}
                    </div>
                    {c.tagline && (
                      <p className="text-sm mt-1 opacity-80">{c.tagline}</p>
                    )}
                    <div
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold group-hover:gap-2 transition-all"
                      style={{ color: "#B25D38" }}
                    >
                      {wired ? "Explore" : "Preview"} <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Coming-soon panel for sections with all-stub children */}
      {children.length > 0 && children.every((c) => !isWired(c.to)) && (
        <section
          className="max-w-6xl mx-auto px-4 pb-16"
          data-testid="fp-section-coming-soon-panel"
        >
          <div
            className="rounded-2xl border p-8 text-center"
            style={{ borderColor: "#C79A3F", background: "#fff" }}
          >
            <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
            <div className="font-display text-xl mb-2" style={{ color: "#1A1613" }}>
              This section is coming together.
            </div>
            <p className="text-sm max-w-xl mx-auto opacity-80 mb-5">
              We're wiring up {section.label.toLowerCase()} with real, verified data.
              In the meantime, explore the parts of Food Pulse already live:
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Link
                to="/food-pulse/businesses"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-white font-semibold"
                style={{ background: "#B25D38" }}
              >
                Business Directory <ArrowRight className="w-3 h-3" />
              </Link>
              <Link
                to="/food-pulse/food"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border"
                style={{ background: "#fff", borderColor: "#C79A3F", color: "#8B5E10" }}
              >
                Food Encyclopedia <ArrowRight className="w-3 h-3" />
              </Link>
              <Link
                to="/food-pulse/articles"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border"
                style={{ background: "#fff", borderColor: "#C79A3F", color: "#8B5E10" }}
              >
                Articles <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
